/**
 * MahnwesenPage — Administration › Mahnwesen (P4-S2, Kap. 17.5).
 *
 * One tab per mandate process: Analysemandat (P-04) and Brokermandat (P-05). Each holds its Mahnwesen
 * parameter set (number of reminders + their Fristen, the Eskalationsfrist, the Team-Lead frist, the
 * Versandfenster) and its Kommunikationsbausteine (P-04: S9/S10/S11; P-05: S13/S14 and the
 * Eingangsbestätigung S21) per Anrede x Segment. Every Frist is customer-adjustable; the
 * Standardwerte are only a starting point (Stefan rule).
 *
 * Read-gated by tenant.read; the saves are gated by tenant.manage (the api is the hard line).
 *
 * [Source: apps/web/src/features/administration/pages/ProzessErinnerungenPage.tsx] (config GET/PUT + form pattern)
 */

import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  MAHNWESEN_FRIST_RANGE,
  MAHNWESEN_ESCALATION_RANGE,
  MAHNWESEN_TEAMLEAD_RANGE,
  buildTextTemplateRef,
  type MahnwesenProcessConfigDto,
  type TextTemplateDto,
} from '@brokly/shared';
import { apiClient } from '../../../lib/api-client';
import { qk } from '../../../lib/query-keys';
import { AdminPageHeader } from '../AdminPageHeader';
import { ADMIN_PAGE_META } from '../adminNav';
import { useMahnwesenConfig, useTextTemplates } from '../hooks';
import { Button, LoadingState, ErrorState, TextInput } from '../components';

const meta = ADMIN_PAGE_META['mahnwesen'];

type ProcessKey = 'P-04' | 'P-05';
const PROCESS_TABS: ReadonlyArray<{ key: ProcessKey; label: string }> = [
  { key: 'P-04', label: 'Analysemandat (P-04)' },
  { key: 'P-05', label: 'Brokermandat (P-05)' },
];
const processLabel = (key: ProcessKey): string => PROCESS_TABS.find((t) => t.key === key)!.label;

/** The parameter form of one process (count, Fristen, Eskalation, Team-Lead, Versandfenster). */
function ParameterForm({ config, processKey }: { config: MahnwesenProcessConfigDto; processKey: ProcessKey }) {
  const qc = useQueryClient();
  const [count, setCount] = useState<2 | 3>(config.reminder_count);
  const [fristDays, setFristDays] = useState<string[]>(config.frist_days.map(String));
  const [escalation, setEscalation] = useState(String(config.escalation_offset_days));
  const [teamlead, setTeamlead] = useState(String(config.teamlead_after_days));
  const [secondary, setSecondary] = useState(config.stage2_secondary_channel);
  const [startHour, setStartHour] = useState(String(config.send_window_start_hour));
  const [endHour, setEndHour] = useState(String(config.send_window_end_hour));
  const [weekdaysOnly, setWeekdaysOnly] = useState(config.send_weekdays_only);
  const [error, setError] = useState<string | null>(null);

  /** Resize the Fristen array when the reminder count changes (grow -> Stefan's optional T+21 when it fits, else last + 7). */
  function changeCount(next: 2 | 3): void {
    setCount(next);
    setFristDays((prev) => {
      if (next === prev.length) return prev;
      if (next < prev.length) return prev.slice(0, next);
      const last = Number(prev[prev.length - 1]);
      const optional = config.optional_third_frist_day;
      const grown = Number.isFinite(last) ? (optional > last ? optional : last + 7) : optional;
      return [...prev, String(grown)];
    });
  }

  const save = useMutation({
    mutationFn: () => {
      const frist = fristDays.map((d) => Number(d));
      const esc = Number(escalation);
      const tl = Number(teamlead);
      const sh = Number(startHour);
      const eh = Number(endHour);
      const allInt = [...frist, esc, tl, sh, eh].every((n) => Number.isInteger(n));
      if (!allInt) throw new Error('Bitte ganze Zahlen eingeben.');
      if (frist.length !== count) throw new Error('Die Anzahl Fristen passt nicht zur Anzahl Erinnerungen.');
      for (const d of frist) {
        if (d < MAHNWESEN_FRIST_RANGE.min || d > MAHNWESEN_FRIST_RANGE.max) {
          throw new Error(`Jede Frist muss zwischen ${MAHNWESEN_FRIST_RANGE.min} und ${MAHNWESEN_FRIST_RANGE.max} Tagen liegen.`);
        }
      }
      for (let i = 1; i < frist.length; i++) {
        if (frist[i]! <= frist[i - 1]!) throw new Error('Die Fristen muessen aufsteigend sein.');
      }
      if (esc < MAHNWESEN_ESCALATION_RANGE.min || esc > MAHNWESEN_ESCALATION_RANGE.max) {
        throw new Error(`Die Eskalationsfrist muss zwischen ${MAHNWESEN_ESCALATION_RANGE.min} und ${MAHNWESEN_ESCALATION_RANGE.max} Tagen liegen.`);
      }
      if (tl < MAHNWESEN_TEAMLEAD_RANGE.min || tl > MAHNWESEN_TEAMLEAD_RANGE.max) {
        throw new Error(`Die Team-Lead-Frist muss zwischen ${MAHNWESEN_TEAMLEAD_RANGE.min} und ${MAHNWESEN_TEAMLEAD_RANGE.max} Tagen liegen.`);
      }
      if (!(sh >= 0 && eh <= 24 && sh < eh)) {
        throw new Error('Das Versandfenster ist ungueltig (Start muss vor Ende liegen).');
      }
      return apiClient.admin.setMahnwesenConfig(
        {
          reminder_count: count,
          frist_days: frist,
          escalation_offset_days: esc,
          teamlead_after_days: tl,
          stage2_secondary_channel: secondary,
          send_window_start_hour: sh,
          send_window_end_hour: eh,
          send_weekdays_only: weekdaysOnly,
        },
        processKey,
      );
    },
    onSuccess: () => {
      setError(null);
      void qc.invalidateQueries({ queryKey: qk.adminMahnwesenConfig(processKey) });
    },
    onError: (e: Error) => setError(e.message),
  });

  return (
    <div data-testid="mahnwesen-params" className="rounded-xl border border-border bg-card p-4">
      <div className="mb-3 flex items-center justify-between gap-3">
        <div className="text-sm font-semibold">{processLabel(processKey)}: Fristen und Eskalation</div>
        {config.used_default && (
          <span data-testid="mahnwesen-standard-hint" className="text-xs text-muted-foreground">
            Standardwerte (noch nicht angepasst)
          </span>
        )}
      </div>

      <div className="mb-4">
        <div className="mb-1 text-xs font-medium text-muted-foreground">Anzahl Erinnerungen</div>
        <select
          aria-label="Anzahl Erinnerungen"
          value={count}
          onChange={(e) => changeCount(Number(e.target.value) as 2 | 3)}
          className="rounded-md border border-border bg-background px-3 py-2 text-sm"
        >
          <option value={2}>2 Erinnerungen</option>
          <option value={3}>3 Erinnerungen</option>
        </select>
      </div>

      <div className="grid gap-3 sm:grid-cols-3">
        {fristDays.map((d, i) => (
          <div key={i}>
            <div className="mb-1 text-xs font-medium text-muted-foreground">{`Frist ${i + 1} (Tage ab Zustellung)`}</div>
            <TextInput
              inputMode="numeric"
              value={d}
              aria-label={`Frist ${i + 1} (Tage)`}
              onChange={(e) => setFristDays((prev) => prev.map((v, j) => (j === i ? e.target.value : v)))}
              className="tabular-nums"
            />
          </div>
        ))}
      </div>

      <div className="mt-3 grid gap-3 sm:grid-cols-2">
        <div>
          <div className="mb-1 text-xs font-medium text-muted-foreground">Eskalationsfrist (Tage nach letzter Erinnerung)</div>
          <TextInput inputMode="numeric" value={escalation} aria-label="Eskalationsfrist (Tage)" onChange={(e) => setEscalation(e.target.value)} className="tabular-nums" />
        </div>
        <div>
          <div className="mb-1 text-xs font-medium text-muted-foreground">Team-Lead-Frist (Tage)</div>
          <TextInput inputMode="numeric" value={teamlead} aria-label="Team-Lead-Frist (Tage)" onChange={(e) => setTeamlead(e.target.value)} className="tabular-nums" />
        </div>
      </div>

      <div className="mt-3 grid gap-3 sm:grid-cols-2">
        <div>
          <div className="mb-1 text-xs font-medium text-muted-foreground">Versandfenster Start (Stunde)</div>
          <TextInput inputMode="numeric" value={startHour} aria-label="Versandfenster Start (Stunde)" onChange={(e) => setStartHour(e.target.value)} className="tabular-nums" />
        </div>
        <div>
          <div className="mb-1 text-xs font-medium text-muted-foreground">Versandfenster Ende (Stunde)</div>
          <TextInput inputMode="numeric" value={endHour} aria-label="Versandfenster Ende (Stunde)" onChange={(e) => setEndHour(e.target.value)} className="tabular-nums" />
        </div>
      </div>

      <div className="mt-3 flex flex-col gap-2">
        <label className="flex items-center gap-2 text-xs text-muted-foreground">
          <input type="checkbox" checked={weekdaysOnly} onChange={(e) => setWeekdaysOnly(e.target.checked)} aria-label="Nur Werktage versenden" />
          Nur an Werktagen versenden
        </label>
        <label className="flex items-center gap-2 text-xs text-muted-foreground">
          <input type="checkbox" checked={secondary} onChange={(e) => setSecondary(e.target.checked)} aria-label="Sekundaerkanal bei Stufe 2" />
          Bei der 2. Erinnerung zusaetzlich ins Kundenportal zustellen
        </label>
      </div>

      {error != null && <div className="mt-2 text-sm text-status-danger">{error}</div>}

      <div className="mt-3 flex justify-end">
        <Button size="sm" disabled={save.isPending} onClick={() => save.mutate()}>
          Speichern
        </Button>
      </div>
    </div>
  );
}

/** One editable Kommunikationsbaustein (subject + body). */
function TextTemplateRow({ tpl }: { tpl: TextTemplateDto }) {
  const qc = useQueryClient();
  const ref = buildTextTemplateRef(tpl);
  const [subject, setSubject] = useState(tpl.subject);
  const [body, setBody] = useState(tpl.body);
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: () => {
      if (subject.trim().length === 0 || body.trim().length === 0) {
        throw new Error('Betreff und Text duerfen nicht leer sein.');
      }
      return apiClient.admin.setTextTemplate({
        process_key: tpl.process_key,
        step_key: tpl.step_key,
        kanal: tpl.kanal,
        lang: tpl.lang,
        anrede: tpl.anrede,
        segment: tpl.segment,
        subject,
        body,
      });
    },
    onSuccess: () => {
      setError(null);
      void qc.invalidateQueries({ queryKey: qk.adminTextTemplates(tpl.process_key) });
    },
    onError: (e: Error) => setError(e.message),
  });

  return (
    <div data-testid={`texttpl-${ref}`} className="rounded-lg border border-border bg-card p-3">
      <div className="mb-2 text-xs font-semibold">{ref}</div>
      <label className="mb-1 block text-xs font-medium text-muted-foreground">Betreff</label>
      <TextInput value={subject} aria-label={`Betreff ${ref}`} onChange={(e) => setSubject(e.target.value)} />
      <label className="mb-1 mt-2 block text-xs font-medium text-muted-foreground">Text</label>
      <textarea
        value={body}
        aria-label={`Text ${ref}`}
        onChange={(e) => setBody(e.target.value)}
        rows={6}
        className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm outline-none focus-visible:ring-2 focus-visible:ring-ring"
      />
      {error != null && <div className="mt-2 text-sm text-status-danger">{error}</div>}
      <div className="mt-2 flex justify-end">
        <Button size="sm" disabled={save.isPending} onClick={() => save.mutate()}>
          Text speichern
        </Button>
      </div>
    </div>
  );
}

/** The text editor of a process: its EMAIL Bausteine x Anrede x Segment (steps without a default text yet are hidden). */
export function TextTemplatesEditor({ templates, processKey }: { templates: TextTemplateDto[]; processKey: ProcessKey | 'P-06' }) {
  const emailRows = useMemo(
    () => templates.filter((t) => t.kanal === 'EMAIL' && (t.source === 'tenant' || t.body.trim() !== '')),
    [templates],
  );
  return (
    <div className="rounded-xl border border-border bg-card p-4">
      <div className="mb-1 text-sm font-semibold">
        {processKey === 'P-06' ? 'Dankesbrief (P-06)' : processKey === 'P-05' ? 'Kundentexte (P-05)' : 'Erinnerungstexte (P-04)'}
      </div>
      <p className="mb-3 text-xs text-muted-foreground">
        {processKey === 'P-06'
          ? 'Ein Text pro Schritt (S9 Brokermandat, S9A Analysemandat), Anrede (Sie/Du) und Segment (KMU/PRIV). Der Text fliesst in den Brief, die E-Mail und das Kundenportal. Die Sätze {{eingang_satz}}, {{betreuung_satz}} und {{portal_satz}} setzt Brokly je nach Variante (manuell, sofort, ohne Portal).'
          : processKey === 'P-05'
          ? 'Ein Text pro Schritt (S13/S14 Erinnerungen, S21 Eingangsbestätigung), Anrede (Sie/Du) und Segment (KMU/PRIV). Der Text fliesst in die E-Mail, das Kundenportal und den Brief.'
          : 'Ein Text pro Schritt (S9/S10/S11), Anrede (Sie/Du) und Segment (KMU/PRIV). Der Text fliesst in die E-Mail und den Brief.'}
      </p>
      <div className="grid gap-3 sm:grid-cols-2">
        {emailRows.map((t) => (
          <TextTemplateRow key={buildTextTemplateRef(t)} tpl={t} />
        ))}
      </div>
    </div>
  );
}

export function MahnwesenPage() {
  const [processKey, setProcessKey] = useState<ProcessKey>('P-04');
  const cfg = useMahnwesenConfig(processKey);
  const tpls = useTextTemplates(processKey);

  // Re-mount the forms with a key when the server data changes, so the uncontrolled local state
  // re-hydrates from the fresh config after a save/invalidation (mirrors ProzessErinnerungenPage).
  const [version, setVersion] = useState(0);
  useEffect(() => {
    if (cfg.data != null) setVersion((v) => v + 1);
  }, [cfg.data]);

  const isLoading = cfg.isLoading || tpls.isLoading;
  const isError = cfg.isError || tpls.isError;

  return (
    <div>
      <AdminPageHeader title={meta.title} description={meta.description} />

      <div role="tablist" aria-label="Prozess" className="mb-4 flex gap-2">
        {PROCESS_TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            role="tab"
            aria-selected={processKey === t.key}
            onClick={() => setProcessKey(t.key)}
            className={`rounded-md border px-3 py-1.5 text-sm ${processKey === t.key ? 'border-primary bg-primary/10 font-medium' : 'border-border'}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {isLoading ? (
        <LoadingState />
      ) : isError || cfg.data == null || tpls.data == null ? (
        <ErrorState />
      ) : (
        <div className="space-y-6">
          <ParameterForm key={`params-${processKey}-${version}`} config={cfg.data} processKey={processKey} />
          <TextTemplatesEditor key={`texts-${processKey}`} templates={tpls.data} processKey={processKey} />
        </div>
      )}
    </div>
  );
}
